/**
 * Whole Life LOC - Transforms
 *
 * Bidirectional mapping between frontend grid (flat) and API (nested) formats.
 * Keep this aligned with WholeLifeBenefitsGrid and WholeLifeBenefitsApi types.
 */

import {
  RfpWholeLifeBenefitEmployeeBenefitAmountType,
  RfpWholeLifeBenefitDependentBenefitType,
} from "@platform/openapi-types";

import {
  shouldIncludeBenefitInApi,
  cleanupNotDisclosedBenefits,
} from "../shared/builders";

import type { WholeLifeBenefitsGrid, WholeLifeBenefitsApi } from "./types";

/**
 * Type for API employeeBenefitAmount.type field
 * Includes only the actual API enum values (not frontend-specific types)
 */
type ApiEmployeeBenefitAmountType =
  RfpWholeLifeBenefitEmployeeBenefitAmountType;

/**
 * Maps frontend-specific types to API enum values
 */
function mapFrontendTypeToApiType(
  frontendType: WholeLifeBenefitsGrid["employeeBenefitAmountType"],
): ApiEmployeeBenefitAmountType {
  if (frontendType === "multipleDollarAmounts") {
    return "multipleOptions" as ApiEmployeeBenefitAmountType;
  } else if (frontendType === "multipleSalaryMultipliers") {
    return "salaryMultiplier" as ApiEmployeeBenefitAmountType;
  }
  return frontendType as ApiEmployeeBenefitAmountType;
}

/**
 * Maps API enum values to frontend-specific types
 */
function mapApiTypeToFrontendType(
  apiType: ApiEmployeeBenefitAmountType | undefined,
  amounts: number[] | undefined,
): WholeLifeBenefitsGrid["employeeBenefitAmountType"] {
  if (apiType === "multipleOptions") {
    return "multipleDollarAmounts";
  } else if (
    apiType === "salaryMultiplier" &&
    Array.isArray(amounts) &&
    amounts.length > 1
  ) {
    return "multipleSalaryMultipliers";
  }
  return apiType;
}

/**
 * Checks if a frontend type should be treated as an array type
 */
function isArrayType(
  frontendType: WholeLifeBenefitsGrid["employeeBenefitAmountType"],
): boolean {
  return (
    frontendType === "multipleDollarAmounts" ||
    frontendType === "multipleSalaryMultipliers" ||
    frontendType === "multipleOptions"
  );
}

/**
 * Transforms employee benefit amount from grid to API format
 */
function transformEmployeeBenefitAmountToApi(
  benefits: WholeLifeBenefitsGrid,
): WholeLifeBenefitsApi["employeeBenefitAmount"] {
  if (!shouldIncludeBenefitInApi(benefits.employeeBenefitAmountType)) {
    return undefined;
  }

  const frontendType = benefits.employeeBenefitAmountType!;
  const apiType = mapFrontendTypeToApiType(frontendType);

  if (isArrayType(frontendType)) {
    // For array types, only include if we have a valid array
    if (
      Array.isArray(benefits.employeeBenefitAmountAmount) &&
      benefits.employeeBenefitAmountAmount.length > 0
    ) {
      return {
        type: apiType,
        amount: (
          benefits.employeeBenefitAmountAmount as { amount: number }[]
        ).map((x) => Number(x.amount)),
      };
    }
  } else {
    // For other types, only include if we have a valid number
    const amountValue = Number(benefits.employeeBenefitAmountAmount);
    if (!isNaN(amountValue) && amountValue > 0) {
      return {
        type: apiType,
        amount: [amountValue],
      };
    }
  }

  return undefined;
}

/**
 * Transforms employee benefit maximum from grid to API format
 */
function transformEmployeeBenefitMaximumToApi(
  benefits: WholeLifeBenefitsGrid,
): WholeLifeBenefitsApi["employeeBenefitMaximum"] {
  if (!shouldIncludeBenefitInApi(benefits.employeeBenefitMaximumType)) {
    return undefined;
  }

  return {
    type: benefits.employeeBenefitMaximumType!,
    ...(benefits.employeeBenefitMaximumType === "dollarAmount" ||
    benefits.employeeBenefitMaximumType ===
      "lesserOfDollarAmountOrSalaryMultiplier"
      ? { dollarAmount: benefits.employeeBenefitMaximumDollarAmount }
      : {}),
    ...(benefits.employeeBenefitMaximumType === "salaryMultiplier" ||
    benefits.employeeBenefitMaximumType ===
      "lesserOfDollarAmountOrSalaryMultiplier"
      ? { salaryMultiplier: benefits.employeeBenefitMaximumSalaryMultiplier }
      : {}),
  };
}

/**
 * Transforms dependent benefit (spouse/child) from grid to API format
 */
function transformDependentBenefitToApi(
  benefitType: RfpWholeLifeBenefitDependentBenefitType | undefined,
  benefitAmount: number | undefined,
):
  | { type?: RfpWholeLifeBenefitDependentBenefitType; amount?: number }
  | undefined {
  if (!shouldIncludeBenefitInApi(benefitType)) {
    return undefined;
  }

  const result: {
    type?: RfpWholeLifeBenefitDependentBenefitType;
    amount?: number;
  } = {
    type: benefitType!,
  };

  // Include amount only if type is dollarAmount AND amount is valid
  if (
    benefitType === "dollarAmount" &&
    benefitAmount != null &&
    benefitAmount > 0
  ) {
    result.amount = benefitAmount;
  }

  return result;
}

/**
 * Transforms employee benefit amount from API to grid format
 */
function transformEmployeeBenefitAmountFromApi(
  apiAmount: WholeLifeBenefitsApi["employeeBenefitAmount"],
): Pick<
  WholeLifeBenefitsGrid,
  "employeeBenefitAmountType" | "employeeBenefitAmountAmount"
> {
  const result: Partial<WholeLifeBenefitsGrid> = {};

  if (!apiAmount) {
    return result;
  }

  const apiType = apiAmount.type;
  const amounts = apiAmount.amount;
  const frontendType = mapApiTypeToFrontendType(apiType, amounts);

  result.employeeBenefitAmountType = frontendType;

  if (Array.isArray(amounts)) {
    if (
      frontendType === "multipleDollarAmounts" ||
      frontendType === "multipleSalaryMultipliers"
    ) {
      // Convert to {amount: value} objects for popover
      result.employeeBenefitAmountAmount = amounts.map((amt: number) => ({
        amount: amt,
      }));
    } else {
      // Single value - extract first element
      result.employeeBenefitAmountAmount = amounts[0];
    }
  }

  return result;
}

/**
 * Transforms employee benefit maximum from API to grid format
 */
function transformEmployeeBenefitMaximumFromApi(
  apiMaximum: WholeLifeBenefitsApi["employeeBenefitMaximum"],
): Pick<
  WholeLifeBenefitsGrid,
  | "employeeBenefitMaximumType"
  | "employeeBenefitMaximumDollarAmount"
  | "employeeBenefitMaximumSalaryMultiplier"
> {
  const result: Partial<WholeLifeBenefitsGrid> = {};

  if (!apiMaximum) {
    return result;
  }

  result.employeeBenefitMaximumType = apiMaximum.type;
  if (apiMaximum.dollarAmount !== undefined) {
    result.employeeBenefitMaximumDollarAmount = apiMaximum.dollarAmount;
  }
  if (apiMaximum.salaryMultiplier !== undefined) {
    result.employeeBenefitMaximumSalaryMultiplier = apiMaximum.salaryMultiplier;
  }

  return result;
}

/**
 * Transforms dependent benefit (spouse/child) from API to grid format
 */
function transformDependentBenefitFromApi(
  apiBenefit:
    | { type?: RfpWholeLifeBenefitDependentBenefitType; amount?: number }
    | undefined,
  typeField: keyof Pick<
    WholeLifeBenefitsGrid,
    | "spouseBenefitAmountType"
    | "spouseBenefitMaximumType"
    | "childBenefitAmountType"
    | "childBenefitMaximumType"
  >,
  amountField: keyof Pick<
    WholeLifeBenefitsGrid,
    | "spouseBenefitAmountAmount"
    | "spouseBenefitMaximumAmount"
    | "childBenefitAmountAmount"
    | "childBenefitMaximumAmount"
  >,
): Partial<WholeLifeBenefitsGrid> {
  const result: Partial<WholeLifeBenefitsGrid> = {};

  if (!apiBenefit) {
    return result;
  }

  if (apiBenefit.type !== undefined) {
    result[typeField] = apiBenefit.type;
  }
  if (apiBenefit.amount !== undefined) {
    result[amountField] = apiBenefit.amount;
  }

  return result;
}

// FE grid -> API object
export function transformWholeLifeBenefitsToApi(
  benefits: WholeLifeBenefitsGrid,
): WholeLifeBenefitsApi {
  const api: WholeLifeBenefitsApi = {
    objectType: "wholeLife",
  };

  // Coverage Type
  if (shouldIncludeBenefitInApi(benefits.coverageType)) {
    api.coverageType = benefits.coverageType;
  }

  // Employee benefits
  api.employeeBenefitAmount = transformEmployeeBenefitAmountToApi(benefits);
  api.employeeBenefitMaximum = transformEmployeeBenefitMaximumToApi(benefits);

  // Guarantee Issue Amount
  if (benefits.guaranteeIssueAmount != null) {
    api.guaranteeIssueAmount = benefits.guaranteeIssueAmount;
  }

  // Dependent benefits
  api.spouseBenefitAmount = transformDependentBenefitToApi(
    benefits.spouseBenefitAmountType,
    benefits.spouseBenefitAmountAmount,
  );
  api.spouseBenefitMaximum = transformDependentBenefitToApi(
    benefits.spouseBenefitMaximumType,
    benefits.spouseBenefitMaximumAmount,
  );
  api.childBenefitAmount = transformDependentBenefitToApi(
    benefits.childBenefitAmountType,
    benefits.childBenefitAmountAmount,
  );
  api.childBenefitMaximum = transformDependentBenefitToApi(
    benefits.childBenefitMaximumType,
    benefits.childBenefitMaximumAmount,
  );

  // Simple fields
  if (benefits.ridersIncluded !== undefined) {
    api.ridersIncluded = benefits.ridersIncluded;
  }
  if (benefits.additionalOptionsIncluded !== undefined) {
    api.additionalOptionsIncluded = benefits.additionalOptionsIncluded;
  }
  if (shouldIncludeBenefitInApi(benefits.portability)) {
    api.portability = benefits.portability;
  }
  if (benefits.interestRate !== undefined) {
    api.interestRate = benefits.interestRate;
  }
  if (benefits.endowsAge !== undefined) {
    api.endowsAge = benefits.endowsAge;
  }
  if (shouldIncludeBenefitInApi(benefits.coverageEffectiveDate)) {
    api.coverageEffectiveDate = benefits.coverageEffectiveDate;
  }
  if (shouldIncludeBenefitInApi(benefits.annualOpenEnrollment)) {
    api.annualOpenEnrollment = benefits.annualOpenEnrollment;
  }

  // Extended (non-v5) fields — CD-390. Sent as a full object (a full-replace
  // PATCH, like the rest of `benefits`) whenever at least one extended field
  // is set, so an edit to any other field on the plan doesn't drop the
  // extended values already on it. Omitted entirely when every extended
  // field is unset, so an edit that never touches these fields doesn't
  // create an empty extended row on the backend.
  const extended = {
    longTermCare: benefits.longTermCare,
  };
  if (
    Object.values(extended).some((value) => value !== undefined && value !== "")
  ) {
    api.extended = extended;
  }

  return cleanupNotDisclosedBenefits(api) as WholeLifeBenefitsApi;
}

// API -> FE grid
export function transformWholeLifeBenefitsFromApi(
  api: WholeLifeBenefitsApi,
): WholeLifeBenefitsGrid {
  const grid: Partial<WholeLifeBenefitsGrid> = {
    objectType: "wholeLife",
  };

  // Only set fields if they exist in API data
  if (api.coverageType !== undefined) grid.coverageType = api.coverageType;

  // Employee benefits
  Object.assign(
    grid,
    transformEmployeeBenefitAmountFromApi(api.employeeBenefitAmount),
  );
  Object.assign(
    grid,
    transformEmployeeBenefitMaximumFromApi(api.employeeBenefitMaximum),
  );

  if (api.guaranteeIssueAmount !== undefined) {
    grid.guaranteeIssueAmount = api.guaranteeIssueAmount;
  }

  // Dependent benefits
  Object.assign(
    grid,
    transformDependentBenefitFromApi(
      api.spouseBenefitAmount,
      "spouseBenefitAmountType",
      "spouseBenefitAmountAmount",
    ),
  );
  Object.assign(
    grid,
    transformDependentBenefitFromApi(
      api.spouseBenefitMaximum,
      "spouseBenefitMaximumType",
      "spouseBenefitMaximumAmount",
    ),
  );
  Object.assign(
    grid,
    transformDependentBenefitFromApi(
      api.childBenefitAmount,
      "childBenefitAmountType",
      "childBenefitAmountAmount",
    ),
  );
  Object.assign(
    grid,
    transformDependentBenefitFromApi(
      api.childBenefitMaximum,
      "childBenefitMaximumType",
      "childBenefitMaximumAmount",
    ),
  );

  // Simple fields - only set if they exist
  if (api.ridersIncluded !== undefined) {
    grid.ridersIncluded = api.ridersIncluded;
  }
  if (api.additionalOptionsIncluded !== undefined) {
    grid.additionalOptionsIncluded = api.additionalOptionsIncluded;
  }
  if (api.portability !== undefined) grid.portability = api.portability;
  if (api.interestRate !== undefined) grid.interestRate = api.interestRate;
  if (api.endowsAge !== undefined) grid.endowsAge = api.endowsAge;
  if (api.coverageEffectiveDate !== undefined) {
    grid.coverageEffectiveDate = api.coverageEffectiveDate;
  }
  if (api.annualOpenEnrollment !== undefined) {
    grid.annualOpenEnrollment = api.annualOpenEnrollment;
  }

  // Extended (non-v5) fields — CD-390
  grid.longTermCare = api.extended?.longTermCare ?? undefined;

  return grid as WholeLifeBenefitsGrid;
}

/** Grid keys that persist under `benefits.extended`. */
export const WHOLE_LIFE_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> = new Set(
  ["longTermCare"],
);
